import { Button, Overlay, TextInput, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { clsx } from 'clsx';
import { IconCheck, IconCopy, IconLinkPlus, IconRefresh } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useMonitoring } from '@monkvision/monitoring';
import styles from './InspectionLinkSingleCreate.module.css';
import { useInspectionLinkSingleCreate } from '../../hooks';
import { CreateInspectionParams } from '../../hooks/useCreateInspectionLink/types';

function isEmpty(value: string | null | undefined): boolean {
  return value === '' || value === null || value === undefined;
}

export function InspectionLinkSingleCreate() {
  const { t } = useTranslation();
  const required = (value?: string) =>
    isEmpty(value) ? t('inspectionLinkCreate.single.errors.required') : null;
  const form = useForm<Partial<CreateInspectionParams>>({
    initialValues: {
      vin: undefined,
    },
    validate: {
      vin: required,
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });
  const { loading, link, generateLink, reset } = useInspectionLinkSingleCreate();
  const [isTooltipOpen, { open: openTooltip, close: closeTooltip }] = useDisclosure(false);
  const { handleError } = useMonitoring();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(link ?? '');
      openTooltip();
      setTimeout(closeTooltip, 1000);
    } catch (err) {
      handleError(err);
    }
  };

  const handleReset = () => {
    reset();
    closeTooltip();
  };

  const handleRetry = () => {
    loading.onSuccess();
  };

  return (
    <form
      className={styles['container']}
      onSubmit={form.onSubmit((values) => generateLink(values as CreateInspectionParams))}
    >
      {link && (
        <Overlay blur={5} color='#FFF' center className={styles['overlay']}>
          <div className={styles['overlayMessage']}>
            {t('inspectionLinkCreate.single.success.message')}
          </div>
          <Tooltip
            label={
              <div className={styles['tooltip']}>
                <IconCheck size={14} />
                <span>{t('inspectionLinkCreate.single.success.tooltip')}</span>
              </div>
            }
            opened={isTooltipOpen}
            withArrow
          >
            <Button
              color='dark'
              leftIcon={<IconCopy size={18} />}
              onClick={handleCopyLink}
              radius='xl'
            >
              {t('inspectionLinkCreate.single.success.copy')}
            </Button>
          </Tooltip>
          <Button
            color='dark'
            variant='outline'
            leftIcon={<IconLinkPlus size={18} />}
            onClick={handleReset}
            className={styles['resetButton']}
          >
            {t('inspectionLinkCreate.single.success.another')}
          </Button>
        </Overlay>
      )}
      {loading.error && (
        <Overlay blur={5} color='#FFF' center className={styles['overlay']}>
          <div className={clsx(styles['overlayMessage'], styles['error'])}>
            {t('inspectionLinkCreate.single.errors.generation')}
          </div>
          <Button
            color='dark'
            variant='outline'
            leftIcon={<IconRefresh size={18} />}
            onClick={handleRetry}
          >
            {t('inspectionLinkCreate.single.errors.tryAgain')}
          </Button>
        </Overlay>
      )}
      <TextInput
        placeholder={t('inspectionLinkCreate.single.vin.placeholder')}
        label={t('inspectionLinkCreate.single.vin.label')}
        withAsterisk
        className={styles['input']}
        disabled={loading.isLoading}
        {...form.getInputProps('vin')}
      />
      <div className={styles['submitContainer']}>
        <Button
          type='submit'
          color='dark'
          leftIcon={<IconLinkPlus size={18} />}
          loading={loading.isLoading}
          disabled={!form.isValid()}
        >
          {t('inspectionLinkCreate.single.submit')}
        </Button>
      </div>
    </form>
  );
}
